import { useState, type FormEvent } from 'react';
import { OperatorErrorText } from './TechnicalDetails';
import { WorkspaceDialog } from './WorkspaceDialog';
import './more-tips.css';

/**
 * "Get more tips like this" (owner, October 4, 2026): a small button on an opened setup tip
 * opens this. The person has just read a tip they wanted to read, so the ask comes right
 * after something useful, dressed as their own cheat sheet (the owner's pick of ten): sticky
 * notes of real shortcuts pop onto a corkboard, and signing up pins "You're in!" on it.
 *
 * It is the same opt-in the first launch used (ADR-0080; nothing in the product asked for it
 * since October 3): it saves the choice on this computer, Phone Home sends the emails, and
 * Settings › License shows it and turns it off. The opt-in needs a name with the email
 * (Phone Home's welcome greets by name; owner: keep the name field).
 */

/** Real shortcuts and features only: each note is something the product does today. */
const NOTES = [
  { x: 22, y: 20, turn: -5, text: 'Ctrl+Enter' },
  { x: 98, y: 14, turn: 4, text: 'Focus' },
  { x: 172, y: 22, turn: -3, text: 'Undo' },
  { x: 52, y: 78, turn: 3, text: 'Lists' },
  { x: 132, y: 76, turn: -4, text: 'Search' },
];

function CheatSheetArt({ done }: { done: boolean }) {
  return (
    <svg className="more-tips-svg" viewBox="0 0 250 140" aria-hidden="true">
      <rect className="more-tips-board" x="6" y="6" width="238" height="128" rx="10" />
      {NOTES.map((note, index) => (
        <g
          key={note.text}
          className="more-tips-note"
          style={{ animationDelay: `${index * 0.32}s` }}
          transform={`translate(${note.x} ${note.y}) rotate(${note.turn})`}
        >
          <rect
            width="58"
            height="46"
            rx="3"
            className={`more-tips-paper more-tips-paper-${index % 3}`}
          />
          <circle className="more-tips-pin" cx="29" cy="6" r="3.4" />
          <text x="29" y="30">
            {note.text}
          </text>
        </g>
      ))}
      {done && (
        <g className="more-tips-note more-tips-yours" transform="translate(150 60) rotate(6)">
          <rect width="82" height="58" rx="3" className="more-tips-paper more-tips-paper-0" />
          <circle className="more-tips-pin" cx="41" cy="7" r="4" />
          <text x="41" y="35">
            You’re in!
          </text>
        </g>
      )}
    </svg>
  );
}

export function MoreTipsAsk({ onClose }: { onClose: () => void }) {
  // PAIGE: the signed-in person fills the form (the host's person). The name can be changed; the
  // address cannot, since Paige's tips always go to the address the person signs in with.
  const person = window.paigeWorkbench?.person;
  const signInAddress = person?.email || null;
  const [name, setName] = useState(person?.name ?? '');
  const [email, setEmail] = useState(person?.email ?? '');
  const [state, setState] = useState<'ask' | 'saving' | 'done'>('ask');
  const [problem, setProblem] = useState('');
  const send = async (event: FormEvent) => {
    event.preventDefault();
    const save = window.capturePointDesktop?.setEngagementConsent;
    if (!save || state === 'saving') return;
    setState('saving');
    setProblem('');
    try {
      await save({ choice: 'opt-in', name, email });
      setState('done');
    } catch (reason) {
      setProblem(reason instanceof Error ? reason.message : 'That didn’t save. Try again.');
      setState('ask');
    }
  };
  const done = state === 'done';
  const headline = done ? 'You’re on the list!' : 'Your cheat sheet, delivered';
  // The dialog keeps its own title for screen readers; the big headline under the picture says it.
  return (
    <WorkspaceDialog
      telemetry="more-tips"
      title={headline}
      compact
      complete={done}
      onClose={onClose}
    >
      <div className="more-tips">
        <div className="more-tips-art">
          <CheatSheetArt done={done} />
        </div>
        <p className="more-tips-headline" aria-hidden="true">
          {headline}
        </p>
        {done ? (
          <>
            <p className="more-tips-line">
              Look for a welcome email soon. Your first note is on its way.
            </p>
            <button type="button" className="more-tips-go" onClick={onClose}>
              Back to setup
            </button>
          </>
        ) : (
          <form onSubmit={(event) => void send(event)}>
            <p className="more-tips-line">Pin-worthy shortcuts, one at a time.</p>
            <label>
              <span>Your name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                maxLength={200}
                required
              />
            </label>
            <label>
              <span>Work email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                maxLength={254}
                required
                readOnly={Boolean(signInAddress)}
                aria-describedby={signInAddress ? 'more-tips-sign-in-address' : undefined}
              />
              {signInAddress && (
                <em id="more-tips-sign-in-address" className="more-tips-address-note">
                  Tips go to the address you sign in with.
                </em>
              )}
            </label>
            {/* A <div>: the message can carry a Technical details disclosure, which a <p> may not hold. */}
            {problem && (
              <div
                className="more-tips-problem"
                role="alert"
                data-telemetry="error:tips-sign-up-failed"
              >
                <OperatorErrorText message={problem} />
              </div>
            )}
            <button type="submit" className="more-tips-go" disabled={state === 'saving'}>
              {state === 'saving' ? 'Pinning…' : 'Start my cheat sheet'}
            </button>
            <small>We’ll never spam you. Unsubscribe any time.</small>
          </form>
        )}
      </div>
    </WorkspaceDialog>
  );
}
